import {
  BulkDeleteArrearsRequestInput,
  BulkPreviewArrearsRequestInput,
  PayrollArrear,
  PayrollFetchArrearsResponse,
  SDIType,
} from 'api/gql-utils/arrears/types';
import { paiseAmountToRupee, rupeeAmountToPaise, uniqueId } from 'utils/Numbers';
import { ArrearsBreakdown, ArrearComponent, ArrearMonth, EditHistory, ArrearsState } from './types';
import { format } from 'date-fns';
import { dateFormats } from 'utils/Dates';
import { getMonthsForPreview } from './state/selectors';
import { statutoryComponentNameMap } from 'components/SalaryStructures/creation';

export const getNewArrearComponent = (): ArrearComponent => {
  return {
    id: uniqueId(),
    componentId: '',
    amount: '',
    componentName: '',
    componentSource: undefined,
    sdiType: undefined,
    isSaved: false,
  };
};

export const getNewArrearMonth = (sourceMonth?: string): ArrearMonth => {
  return {
    arrearId: uniqueId(),
    sourceMonth: sourceMonth ?? '',
    isSaved: false,
    components: [getNewArrearComponent()],
    arrearDays: '',
  };
};

export const isComplianceComponent = (sdiType: SDIType | undefined) => {
  if (!sdiType) {
    return false;
  }
  return sdiType === 'esi_v1' || sdiType === 'pf_v1';
};

export const getComponentName = ({ sdiType, name }: { sdiType?: SDIType; name: string }) => {
  if (isComplianceComponent(sdiType)) {
    return statutoryComponentNameMap[name as keyof typeof statutoryComponentNameMap] ?? name;
  }
  return name;
};

export const getAmountBasedOnType = (sdiType: SDIType, formulae?: Record<string, unknown>) => {
  return isComplianceComponent(sdiType)
    ? paiseAmountToRupee(Number(formulae?.employer_amount ?? 0)) //converting to rupees
    : paiseAmountToRupee(Number(formulae?.amount ?? 0)); //converting to rupees
};

export const getCombinedArrears = (
  data?: PayrollFetchArrearsResponse,
): Array<{
  id: string;
  component: string;
  arrearAmount: number;
  sdiType: SDIType;
}> => {
  const combinedArrear = data?.payrollFetchArrears?.data?.arrears?.reduce(
    (acc, arrear) => {
      arrear.arrearComponents?.forEach((component) => {
        const amount = getAmountBasedOnType(component.sdiType, component.formulae);
        if (acc[component.componentId]) {
          acc[component.componentId].arrearAmount += amount;
        } else {
          acc[component.componentId] = {
            id: component.componentId,
            component: component.componentName,
            arrearAmount: amount,
            sdiType: component.sdiType,
          };
        }
      });
      return acc;
    },
    {} as Record<
      string,
      {
        id: string;
        component: string;
        arrearAmount: number;
        sdiType: SDIType;
      }
    >,
  );

  const arrears = Object.values(combinedArrear || {});
  const sortedArrears = arrears.sort((component1, component2) => {
    // Basic comes first
    if (component1.component === 'Basic') return -1;
    if (component2.component === 'Basic') return 1;

    // Compliance components come last
    const isComponent1Compliance = isComplianceComponent(component1.sdiType);
    const isComponent2Compliance = isComplianceComponent(component2.sdiType);

    if (isComponent1Compliance && !isComponent2Compliance) return 1;
    if (!isComponent1Compliance && isComponent2Compliance) return -1;

    // Special Allowance comes just before compliance
    if (component1.component === 'Special Allowance' && !isComponent2Compliance) return 1;
    if (component2.component === 'Special Allowance' && !isComponent1Compliance) return -1;

    return 0;
  });
  return sortedArrears;
};

export const getArrearsBreakdownInitialValue = (
  data?: PayrollArrear[],
): ArrearsBreakdown | undefined => {
  return data?.reduce((acc: ArrearsBreakdown, arrear) => {
    acc[arrear.id] = {
      arrearId: arrear.id,
      sourceMonth: arrear.sourceMonth,
      components:
        arrear.arrearComponents?.map((component) => ({
          id: component.id,
          componentId: component.componentId,
          amount: getAmountBasedOnType(component.sdiType, component.formulae),
          componentName: component.componentName ?? '',
          componentSource: component.componentSource ?? undefined,
          sdiType: component.sdiType,
          isSaved: true,
        })) || [],
      isSaved: true,
      arrearDays: arrear.arrearDays,
    };
    return acc;
  }, {} as ArrearsBreakdown);
};

export const getBulkPreviewArrearsMutationPayload = ({
  orgId,
  requesterPeopleId,
  arrearsBreakdown,
  editHistory,
  payrollMonth,
  userId,
}: {
  orgId: string; //org_123
  requesterPeopleId: string; //user_123
  arrearsBreakdown: ArrearsBreakdown;
  editHistory: EditHistory;
  payrollMonth: string;
  userId: string; //user_123
}): BulkPreviewArrearsRequestInput => {
  // Send only months that have been edited (not deleted)
  const monthsToSend = Object.keys(editHistory).filter((arrearId) => {
    const history = editHistory[arrearId];
    return history.fieldsChanged.size > 0 && !history.isDeleted;
  });

  const payload: BulkPreviewArrearsRequestInput = {
    merchantId: orgId,
    tenantId: orgId,
    requestBy: requesterPeopleId,
    shouldCacheResponse: true,
    userArrearRequests: monthsToSend.map((arrearId) => {
      return {
        userId: userId,
        targetMonth: format(new Date(payrollMonth), dateFormats.yearMonth),
        sourceMonth: format(
          new Date(arrearsBreakdown[arrearId]?.sourceMonth),
          dateFormats.yearMonth,
        ),
        arrearCause: 'ARREAR_CAUSE_PLATFORM',
        arrearDays: Number(arrearsBreakdown[arrearId]?.arrearDays ?? 0),
        arrearAssignments: arrearsBreakdown[arrearId]?.components
          .filter((component) => !isComplianceComponent(component.sdiType))
          .map((component) => {
            return {
              componentId: component.componentId,
              value: rupeeAmountToPaise(Number(component.amount ?? 0)).toString(), //in paise
              fieldIdentifier: '',
              includeInPayroll: true,
              includeInCompensation: false,
              componentSource: component.componentSource ?? 'COMPONENT_SOURCE_SALARY_STRUCTURE',
            };
          }),
      };
    }),
  };
  return payload;
};

export const getBulkDeleteArrearsMutationPayload = ({
  orgId,
  editHistory,
}: {
  orgId: string; //org_123
  editHistory: EditHistory;
}): BulkDeleteArrearsRequestInput => {
  const deletedArrearIds = Object.keys(editHistory).filter(
    (arrearId) => editHistory[arrearId].isDeleted,
  );

  return {
    merchantId: orgId,
    tenantId: orgId,
    deletionTrigger: 'ARREAR_DELETION_TRIGGER_MANUAL',
    deletionCriteria: {
      payrollArrearIds: deletedArrearIds,
    },
  };
};

export const validateArrearsForm = (state: ArrearsState) => {
  const monthsForPreview = getMonthsForPreview(state);
  if (monthsForPreview.length === 0) {
    return '';
  }

  let error = '';

  //check if all the form fields are filled
  monthsForPreview.forEach((arrearId, idx) => {
    const arrear = state.months[arrearId];

    if (!arrear.sourceMonth) {
      error = error + `Source month is required in table ${idx + 1}. `;
      return error;
    }
    const formattedSourceMonth = format(new Date(arrear.sourceMonth), dateFormats.monthYear);

    const componentErrors = [];

    if (!arrear.arrearDays || arrear.arrearDays == 0) {
      componentErrors.push(' the number of arrear days');
    }

    if (arrear.components.length === 0) {
      componentErrors.push(' the components');
    }

    const hasMissingComponentData =
      arrear.components?.length &&
      arrear.components?.some(
        (component) =>
          !isComplianceComponent(component?.sdiType) &&
          (!component?.amount || !component?.componentId),
      );

    if (!!hasMissingComponentData) {
      componentErrors.push(' the component or amount');
    }

    if (componentErrors.length) {
      error = error + `${formattedSourceMonth} is missing` + componentErrors.join(',') + '. ';
    }
  });

  return error;
};
